import { computed, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useUserAuthorizationStore } from '@/stores/userAuthorizationStore';
import {
  getLicenses as apiGetAccountLicenses,
  getUserLicenses as apiGetUserLicenses,
  getArchivedLicenses as apiGetArchivedLicenses,
  getLicensesSummaryInfo,
  getLicensesUsersAssigned,
  getSubscriptionLevel,
  getSubscriptionUpgradeUrl,
  cancelSubscription as apiCancelSubscription,
  type SubscriptionInfo,
  type License,
  type LicenseGroup,
  type ListOfLicenses,
  type UsersAssignedCount,
  DashboardPermission,
} from '@/helpers/apiDashboardWrappers';

export interface SubscriptionData {
  subscriptionLevelId: number;
  subscriptionName: string;
  activeLicenses: LicenseGroup[];
  offSubscriptionLicenses: LicenseGroup[];
  assignedActiveLicensesPromise: Promise<UsersAssignedCount[]>;
  assignedInactiveLicensesPromise: Promise<UsersAssignedCount[]>;
}

export const useSubscriptionStore = defineStore('subscription', () => {
  const subscriptionLevelId = ref(undefined as number | undefined);
  const upgradeUrl = ref(undefined as string | undefined);
  const subscriptions = ref<SubscriptionInfo[]>([]);

  const { permissions } = storeToRefs(useUserAuthorizationStore());

  async function getAccountLicenses() {
    return getLicensesInternal(apiGetAccountLicenses());
  }

  async function getUserLicenses() {
    return getLicensesInternal(apiGetUserLicenses());
  }

  async function getLicensesInternal(licensesPromise: Promise<ListOfLicenses>) {
    try {
      const licenses = await licensesPromise;
      const activeLicensesIds = getLicenseGroupIds(licenses.activeLicenses ?? [], l => l.viewAttributes != null && l.viewAttributes['ViewAssignedUsers'] === true);
      const inactiveLicensesIds = getLicenseGroupIds(licenses.inactiveLicenses ?? [], l => l.viewAttributes != null && l.viewAttributes['ViewAssignedUsers'] === true);

      const assignedActiveLicensesPromise =
        activeLicensesIds.length > 0 && permissions.value.includes(DashboardPermission.LicenseView)
          ? getLicensesUsersAssigned(activeLicensesIds)
          : Promise.resolve([]);

      const assignedInactiveLicensesPromise =
        inactiveLicensesIds.length > 0 && permissions.value.includes(DashboardPermission.LicenseView)
          ? getLicensesUsersAssigned(inactiveLicensesIds)
          : Promise.resolve([]);

      subscriptionLevelId.value = licenses.subscriptionLevelId;

      return {
        subscriptionLevelId: licenses.subscriptionLevelId,
        subscriptionName: licenses.subscriptionLevelName,
        activeLicenses: licenses.activeLicenses,
        offSubscriptionLicenses: licenses.inactiveLicenses,
        assignedActiveLicensesPromise,
        assignedInactiveLicensesPromise,
      };
    }
    catch {
      return undefined;
    }
  }

  async function getArchivedLicenses() {
    try {
      return await apiGetArchivedLicenses();
    }
    catch {
      return undefined;
    }
  }

  async function getAccountSubscriptionLevel() {
    try {
      return await getSubscriptionLevel();
    }
    catch {
      return undefined;
    }
  }

  function getLicenseGroupIds(licenses: LicenseGroup[], predicate: (license: License) => boolean = _ => true) {
    return licenses
      .map((group) => group.licenses ?? [])
      .reduce((a, b) => a.concat(b), [])
      .filter((l) => l.id != null)
      .filter(predicate)
      .map((l) => l.id!);
  }

  async function initSubscriptionInfo() {
    try {
      subscriptionLevelId.value = await getSubscriptionLevel();
      upgradeUrl.value = await getSubscriptionUpgradeUrl();
      return true;
    }
    catch {
      return false;
    }
  }

  async function getSummaryInfo() {
    try {
      return await getLicensesSummaryInfo();
    }
    catch {
      return undefined;
    }
  }

  async function cancelSubscription(id: string) {
    return await apiCancelSubscription(id);
  }

  return {
    subscriptionLevelId: computed(() => subscriptionLevelId.value),
    upgradeUrl: computed(() => upgradeUrl.value),
    subscriptions: computed(() => subscriptions.value),

    getAccountLicenses,
    getUserLicenses,
    getArchivedLicenses,
    getAccountSubscriptionLevel,
    initSubscriptionInfo,
    getSummaryInfo,
    cancelSubscription
  };
});
